import React, { Suspense, useEffect, useState } from "react";
import { Platform } from "react-native";

import eventEmitter from "../../../../../shared/util/eventEmitter";
import { useViewjobProctorAllocation } from "../../../../proctor/__generated__/ViewjobProctorContext";
import { JsjPostApplyAdTst } from "../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { useViewjobOneGraphModel } from "../../../context/ViewJobDataContext";
import { tryConsumePostApplyAdPending } from "./postApplyAdPending";

const PostApplyAdContent = React.lazy(() =>
  import(/* webpackChunkName: "post-apply-ad" */ "./PostApplyAd").then((module) => ({
    default: module.PostApplyAd,
  })),
);

/**
 * Keeps the PostApply JobCard and GraphQL graph out of the initial ViewJob
 * bundle. The full feature is loaded only once a 3P apply is dispatched — at
 * apply-start, so the mounted component can prefetch during the away window.
 */
export function LazyPostApplyAd() {
  const allocation = useViewjobProctorAllocation(JsjPostApplyAdTst);
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  // Two independent facts rather than one "why did we load" tri-state:
  // `loadedAtApplyStart` decides whether the mounted component prefetches, `returned`
  // decides whether it runs the opportunity immediately. Both can be true at once —
  // a jobseeker who gets back while this chunk is still in flight needs the prefetch
  // and the trigger on the same mount. (RN-7827)
  const [loadedAtApplyStart, setLoadedAtApplyStart] = useState(false);
  const [returned, setReturned] = useState(false);
  const shouldLoad = loadedAtApplyStart || returned;
  const isEnabled =
    allocation.value === JsjPostApplyAdTst.buckets.Active ||
    allocation.value === JsjPostApplyAdTst.buckets.Testingonly;

  // Both platforms: load on apply-start, while the jobseeker is on their way to the
  // employer site. PostApplyAd's prefetch only exists once this chunk is mounted, so
  // waiting for the return trigger below made the prefetch unreachable and left a
  // from-scratch request to beat GRACE_WINDOW_MS. Loading here costs no initial-bundle
  // weight — the chunk is still fetched only after an apply click. (RN-7827)
  useEffect(() => {
    if (!isEnabled || loadedAtApplyStart) {
      return;
    }

    const handler = ({ mobvjtk }: { jobKey: string; mobvjtk: string }) => {
      if (mobvjtk === viewjobOneGraphModel?.mobvjtk) {
        setLoadedAtApplyStart(true);
      }
    };
    eventEmitter.on("externalApplyStart", handler);
    return () => eventEmitter.off("externalApplyStart", handler);
  }, [isEnabled, loadedAtApplyStart, viewjobOneGraphModel?.mobvjtk]);

  // Return trigger. Gated on `returned` rather than `shouldLoad` so it stays attached
  // through the apply-start chunk load: React.lazy has not mounted PostApplyAd yet at
  // that point, so if this listener detached, a jobseeker returning mid-load would hit
  // no listener at all and the ad would never open. It also still covers the cases
  // where nothing loaded at apply-start — an Android RN-context teardown while the
  // WebView was up, a same-tab apply navigation that reloaded the page, or a bfcache
  // restore. Once PostApplyAd is mounted both it and this listener are live, but
  // tryConsumePostApplyAdPending is an atomic read-and-clear, so exactly one wins:
  // whichever it is runs the opportunity once. (RN-7827)
  useEffect(() => {
    if (!isEnabled || returned || Platform.OS !== "web" || typeof document === "undefined") {
      return;
    }

    const tryLoad = () => {
      if (tryConsumePostApplyAdPending()) {
        setReturned(true);
      }
    };
    // visibilitychange only fires on a transition, so "visible" already means the
    // tab was hidden and has come back. The pending flag is the only other gate.
    const onVisibility = () => {
      if (document.visibilityState === "visible") tryLoad();
    };
    const onPageshow = (event: PageTransitionEvent) => {
      if (event.persisted) tryLoad();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageshow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageshow);
    };
  }, [isEnabled, returned]);

  // Native return trigger, and unlike the web listener above this one detaches as soon
  // as anything loads the chunk. There is no pending flag on native to arbitrate with —
  // PostApplyAd has its own externalApplyReturn listener, so leaving this attached past
  // mount would run the opportunity twice. Metro resolves the dynamic import out of the
  // bundle in a microtask, so there is no meaningful mid-load window to cover here.
  useEffect(() => {
    if (!isEnabled || shouldLoad || Platform.OS === "web") {
      return;
    }

    const handler = ({ mobvjtk }: { jobKey: string; mobvjtk: string }) => {
      if (mobvjtk === viewjobOneGraphModel?.mobvjtk) {
        setReturned(true);
      }
    };
    eventEmitter.on("externalApplyReturn", handler);
    return () => eventEmitter.off("externalApplyReturn", handler);
  }, [isEnabled, shouldLoad, viewjobOneGraphModel?.mobvjtk]);

  if (!shouldLoad) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <PostApplyAdContent prefetchOnMount={loadedAtApplyStart} initiallyTriggered={returned} />
    </Suspense>
  );
}
